import {type DependencyList, useEffect, useCallback, useMemo} from 'react';

import {subscribe, unsubscribe} from 'web-app/react/components/higher-order/update-on-actions';
import {type NonEmptyArray} from 'web-app/util/non-empty-array';
import {type ActionDescription} from 'web-app/util/redux/action-types';
import {hasValue} from '@famly/stat_ts-utils_has-value';

/**
 * @description
 * Subscribe to actions directly in your components!
 * Supply a non-empty array of actions, a callback,
 * and a dependency list of your callback similar to `useCallback`
 *
 * @example
 * ```
 * useOnActions([Actions.foo], (action, state) => {
 *   console.log('action fired', action.payload, props.foobar)
 * }, [props.foobar])
 * ```
 */

const IsActionDescription = (action: string | ActionDescription<any>): action is ActionDescription<any> =>
    typeof action !== 'string';
const IsActionType = (action: string | ActionDescription<any>): action is string => typeof action === 'string';

export function useOnActions<T>(
    callback: (action: T, state: any) => void,
    dependencyList: DependencyList,
    actions: NonEmptyArray<string>,
): void;
export function useOnActions<T extends ActionDescription<any>>(
    callback: (action: ReturnType<T['action']>, state: any) => void,
    dependencyList: DependencyList,
    actions: NonEmptyArray<T>,
): void;

export function useOnActions<T extends string | ActionDescription<any>>(
    /**
     * `state` is the entire redux state of the project you're using this in.
     * We've left it untyped so it can be shared between projects
     */
    callback: (action: T extends ActionDescription<any> ? ReturnType<T['action']> : any, state: any) => void,
    dependencyList: DependencyList,
    actions: NonEmptyArray<T>,
) {
    /**
     * This can't be statically linted so we tell react-hooks plugin to skip it
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoedActions = useMemo(() => actions, actions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoedCallback = useCallback(callback, dependencyList);

    useEffect(() => {
        const subscription = subscribe(
            memoedActions
                .map(action => {
                    if (IsActionDescription(action)) {
                        return action.type;
                    } else if (IsActionType(action)) {
                        return action;
                    }
                    return null;
                })
                .filter(hasValue),
            memoedCallback,
        );
        return () => unsubscribe(subscription);
    }, [memoedActions, memoedCallback]);
}
